import { format } from "date-fns";

import {
  CASE_DOCUMENT_STEP,
  CASE_FILE_KIND,
  CASE_FILE_STATUS,
  CaseDocumentFileModel,
  CaseDocumentModel,
} from "@shared/models";
import { bytesToMegaBytes, getUSFormattedDate } from "@shared/utils";
import { pluralize } from "@containers/Cases/utils/caseDocuments";

const FILE_TYPE_LABELS: Record<string, string> = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "image/jpeg": "JPG",
  "image/png": "PNG",
  "text/plain": "TXT",
};

export const getFileTypeLabel = (file: Pick<CaseDocumentFileModel, "mime_type">) =>
  FILE_TYPE_LABELS[file.mime_type] ?? "FILE";

/** Browsers can't show these in a tab, so viewing one downloads it. */
export const isDownloadOnlyFile = (file: Pick<CaseDocumentFileModel, "mime_type">) => getFileTypeLabel(file) === "DOCX";

export const formatMegaBytes = (bytes: number) => `${Number(bytesToMegaBytes(bytes).toFixed(1))} MB`;

export const formatFileSize = (bytes: number) => {
  const megaBytes = bytesToMegaBytes(bytes);

  return megaBytes >= 1 ? `${megaBytes.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const sumFiles = (files: CaseDocumentFileModel[], key: "pages" | "size") =>
  files.every((file) => file[key] !== null) ? files.reduce((sum, file) => sum + (file[key] ?? 0), 0) : null;

/** E.g. "Oct 5, 2026". */
export const formatDocumentDate = (date: string) => format(new Date(date), "MMM d, yyyy");

export const isFailedFile = ({ status }: CaseDocumentFileModel) => status === CASE_FILE_STATUS.FAILED;

const getUploadedLabel = ({ kind, files }: CaseDocumentModel) => {
  const uploadedAt = files.reduce(
    (latest, file) => (file.uploaded_at > latest ? file.uploaded_at : latest),
    files[0].uploaded_at,
  );

  return kind === CASE_FILE_KIND.PSR
    ? `Uploaded ${getUSFormattedDate(uploadedAt)}`
    : `Added ${formatDocumentDate(uploadedAt)}`;
};

const getFilesMeta = (files: CaseDocumentFileModel[]) => {
  const pages = sumFiles(files, "pages");
  const size = sumFiles(files, "size");

  return [pages !== null && pluralize(pages, "page"), size !== null && formatFileSize(size)];
};

const getMedicalFilesMeta = (files: CaseDocumentFileModel[]) => {
  const parsedFiles = files.filter((file) => !isFailedFile(file));
  const pages = sumFiles(parsedFiles, "pages");

  return [`${pluralize(parsedFiles.length, "file")} parsed`, pages !== null && pluralize(pages, "page")];
};

export const getCaseDocumentMeta = (caseDocument: CaseDocumentModel) => {
  const { kind, files } = caseDocument;

  return [
    ...(kind === CASE_FILE_KIND.MEDICAL ? getMedicalFilesMeta(files) : getFilesMeta(files)),
    getUploadedLabel(caseDocument),
  ]
    .filter(Boolean)
    .join(" · ");
};

const PARSING_PHASES: Record<CASE_DOCUMENT_STEP, string> = {
  [CASE_DOCUMENT_STEP.READING]: "reading text",
  [CASE_DOCUMENT_STEP.EXTRACTING]: "filling fields",
  [CASE_DOCUMENT_STEP.CHECKING]: "running checks",
};

/**
 * 0–100. Until the API reports progress, it's estimated from the files read: reading takes most of the time, and
 * the rest stays short of 100 until parsing is done.
 */
export const getParsingProgress = ({ progress, step, files_read, files }: CaseDocumentModel) => {
  if (progress !== undefined) return progress;
  if (step === CASE_DOCUMENT_STEP.EXTRACTING || step === CASE_DOCUMENT_STEP.CHECKING) return 95;

  return files.length ? Math.round((files_read / files.length) * 90) : 0;
};

/** E.g. "reading text". */
export const getParsingPhase = ({ step }: CaseDocumentModel) => PARSING_PHASES[step ?? CASE_DOCUMENT_STEP.READING];
